import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type Tone = 'neutral' | 'ok' | 'warn' | 'crit' | 'info' | 'copper';

export interface PillProps {
  tone?: Tone;
  children: ReactNode;
  title?: string;
}

/** 999px radius. Status, never an action. */
export function Pill({ tone = 'neutral', children, title }: PillProps) {
  return (
    <span className={`o-pill o-pill--${tone}`} {...(title ? { title } : {})}>
      {children}
    </span>
  );
}

export interface ChipProps {
  label: string;
  active?: boolean;
  count?: number;
  onClick?: () => void;
  href?: string;
  title?: string;
}

/** 4px radius. Filter chips and sub-nav segments. Always a real control. */
export function Chip({ label, active = false, count, onClick, title }: ChipProps) {
  return (
    <button
      type="button"
      className="o-chip"
      aria-pressed={active}
      onClick={onClick}
      {...(title ? { title } : {})}
    >
      <span>{label}</span>
      {count !== undefined ? <span className="o-chip__count">{count}</span> : null}
    </button>
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'danger' | 'ghost';
  size?: 'md' | 'sm';
  busy?: boolean;
}

/**
 * The only button in the product. A spinner lives inside a button during an action and
 * nowhere else; content areas get skeletons.
 */
export function Button({
  variant = 'default',
  size = 'md',
  busy = false,
  disabled,
  children,
  className,
  ...rest
}: ButtonProps) {
  const classes = [
    'o-btn',
    variant === 'default' ? '' : `o-btn--${variant}`,
    size === 'sm' ? 'o-btn--sm' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classes}
      disabled={disabled === true || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className="o-btn__spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}
